import { alsHtml, textVonHtml } from './als-html.js';
import type { Zerlegung } from './bloecke.js';
import { ersteUeberschrift, type Segment, zusammen } from './markdown-segmente.js';

/**
 * Bildet Blockaenderungen der Oberflaeche auf die Abschnitte (Segmente) ab, die n8n kennt.
 * Die Segmente bleiben die Quelle der Wahrheit: `absenden` vergleicht sie byte-genau mit
 * `urSegmente`, `herkunft` ordnet sie den Abschnitts-IDs zu (Ruling 34). Deshalb aendert
 * hier nichts die ZAHL oder REIHENFOLGE der Segmente -- ein ganz entfernter Abschnitt bleibt
 * als leeres Segment stehen (Spec §5).
 */

export interface Spanne {
  von: number;
  bis: number;
}

export interface Ersetzung extends Spanne {
  neu: string;
}

/** Spanne jedes Segments in `zusammen(segmente)`; leere Segmente haben eine leere Spanne. */
export function segmentGrenzen(segmente: readonly Segment[]): Spanne[] {
  let position = 0;
  let schonInhalt = false;
  return segmente.map((segment) => {
    if (segment.markdown === '') return { von: position, bis: position };
    if (schonInhalt) position += 1;
    const von = position;
    position += segment.markdown.length;
    schonInhalt = true;
    return { von, bis: position };
  });
}

/**
 * Index des nicht leeren Segments, in dem `position` liegt; -1, wenn keines. S2 (strukturelle
 * Runde): Die Spanne ist halboffen -- das Trenn-"\n", das `zusammen()` zwischen zwei Segmenten
 * setzt, und jede Position am oder hinter dem Textende gehoeren zu KEINEM Segment. Frueher
 * lieferte beides stillschweigend das vorige Segment.
 */
export function abschnittVon(grenzen: readonly Spanne[], position: number): number {
  return grenzen.findIndex((g) => g.bis > g.von && g.von <= position && position < g.bis);
}

const ATX_UEBERSCHRIFT = /^#{1,6}(\s|$)/;

export function wendeAn(segmente: Segment[], ersetzungen: readonly Ersetzung[]): Segment[] {
  const grenzen = segmentGrenzen(segmente);
  const jeSegment = new Map<number, Ersetzung[]>();
  for (const e of ersetzungen) {
    const i = grenzen.findIndex((g) => g.bis > g.von && g.von <= e.von && e.bis <= g.bis);
    if (i === -1) {
      throw new Error(`Ersetzung ${e.von}-${e.bis} liegt nicht in genau einem Abschnitt.`);
    }
    jeSegment.set(i, [...(jeSegment.get(i) ?? []), e]);
  }
  const ergebnis = segmente.map((segment, i) => {
    const eigene = jeSegment.get(i);
    const grenze = grenzen[i];
    if (!eigene || !grenze) return segment;
    let markdown = segment.markdown;
    // Von hinten nach vorn, damit fruehere Offsets gueltig bleiben.
    for (const e of [...eigene].sort((a, b) => b.von - a.von)) {
      markdown =
        markdown.slice(0, e.von - grenze.von) +
        e.neu.replace(/\r\n/g, '\n') +
        markdown.slice(e.bis - grenze.von);
    }
    // Bleibt nur Leerraum, gilt der Abschnitt als leer -- sonst stuende eine Geisterzeile im Text.
    if (markdown.trim() === '') markdown = '';
    // A1 (Codex Fix-Runde 3): `ueberschrift`/`ebene` sind Ableitungen der ERSTEN Zeile des
    // Segment-Markdowns -- eine Ersetzung kann genau diese Zeile treffen (Ueberschrift
    // umgeschrieben oder entfernt). Ohne Neuberechnung bliebe hier die Angabe von VOR der
    // Aenderung stehen, obwohl `markdown` schon den neuen Stand traegt. Dieselbe Logik wie
    // `schneideNachSegmentierung` (`ersteUeberschrift`), nicht dupliziert.
    return { ...segment, markdown, ...ersteUeberschrift(markdown) };
  });

  return sichereTrennungen(ergebnis, (i) => jeSegment.has(i));
}

/** Das Markdown eines Segments neu setzen -- samt `ueberschrift`/`ebene` wie in A1. */
function mitMarkdown(segment: Segment, markdown: string): Segment {
  return { ...segment, markdown, ...ersteUeberschrift(markdown) };
}

/**
 * S1 (strukturelle Runde nach Codex Runde 3): EINE Grenzpruefung ueber den Endstand statt
 * zweier Reparaturschleifen (frueher Regel (b) am geaenderten Folgesegment und A2 am
 * geaenderten Vorgaenger -- beide uebersahen den Fall, dass nur ein Segment DAZWISCHEN geleert
 * wird).
 *
 * `zusammen()` setzt zwischen zwei nicht leeren Segmenten genau EIN "\n". Endet der Vorgaenger
 * P nicht auf "\n" und beginnt der Nachfolger N weder mit "\n" noch mit einer ATX-Ueberschrift,
 * ergibt das nur einen weichen Zeilenumbruch, und `marked` verschmilzt die Absaetze. Leere
 * Segmente dazwischen zaehlen nicht, `zusammen()` laesst sie samt Trennzeile weg.
 *
 * Repariert wird genau EINE Seite, und zwar bevorzugt ein Segment, das sich in diesem Aufruf
 * ohnehin aendert: N, sonst P, sonst (nur ein Segment dazwischen wurde geleert) wieder N.
 * Der letzte Fall verletzt die Byte-Treue nicht: Ein NIE beruehrtes Segment, das nicht das
 * erste ist, beginnt immer mit einer Ueberschrift, weil `schneide`, `schneideAnUrsprung` und
 * `schneideNachSegmentierung` (Laengen aus einem frueheren Schnitt) nur dort schneiden. Klebt N,
 * hat es seine Ueberschrift also frueher schon verloren und steht ohnehin in
 * `bearbeiteteAbschnitte`.
 *
 * Unveraenderte Segmente bleiben dieselben Objekte; P wird nur hinten, N nur vorn angefasst,
 * deshalb genuegt ein Durchlauf.
 */
function sichereTrennungen(segmente: Segment[], geaendert: (index: number) => boolean): Segment[] {
  let vorgaenger = -1;
  for (let i = 0; i < segmente.length; i += 1) {
    const n = segmente[i]!;
    if (n.markdown === '') continue;
    const p = segmente[vorgaenger];
    const ersteZeile = n.markdown.split('\n')[0] ?? '';
    if (
      p &&
      !p.markdown.endsWith('\n') &&
      !n.markdown.startsWith('\n') &&
      !ATX_UEBERSCHRIFT.test(ersteZeile)
    ) {
      if (!geaendert(i) && geaendert(vorgaenger)) {
        segmente[vorgaenger] = mitMarkdown(p, `${p.markdown}\n`);
      } else {
        segmente[i] = mitMarkdown(n, `\n${n.markdown}`);
      }
    }
    vorgaenger = i;
  }
  return segmente;
}

function pruefeZerlegung(segmente: Segment[], zerlegung: Zerlegung): void {
  // Die Offsets der Zerlegung gelten nur fuer genau diesen Text. Eine veraltete Zerlegung
  // (nach einer Aenderung nicht neu berechnet) wuerde fremde Stellen ueberschreiben.
  if (zerlegung.text !== zusammen(segmente)) {
    throw new Error('Die Zerlegung passt nicht mehr zum Text der Abschnitte.');
  }
}

export function ersetzeBlock(
  segmente: Segment[],
  zerlegung: Zerlegung,
  blockIndex: number,
  neuRaw: string,
): Segment[] {
  pruefeZerlegung(segmente, zerlegung);
  const block = zerlegung.bloecke[blockIndex];
  if (!block) return segmente;
  if (neuRaw.trim() === '') return entferneBloecke(segmente, zerlegung, [blockIndex]);
  if (neuRaw === block.raw) return segmente;
  // `marked` haengt das Trennzeichen, das `zusammen()` zwischen zwei Segmenten setzt, gern an
  // den LETZTEN Block eines Segments an (derselbe n8n-Parser-Effekt wie bei den Baustein-
  // Markern) -- `block.bis` kann so ueber die Segmentgrenze hinausragen. `wendeAn` erlaubt
  // aber nur Ersetzungen INNERHALB genau eines Abschnitts, deshalb wird die Spanne hier auf
  // die Segmentgrenze gekappt; der ueberstehende Teil ist immer reiner Leerraum.
  const grenzen = segmentGrenzen(segmente);
  const grenze = grenzen[abschnittVon(grenzen, block.von)];
  let bis = block.bis;
  let neu = neuRaw;
  if (grenze && bis > grenze.bis) {
    const ueberhang = zerlegung.text.slice(grenze.bis, bis);
    bis = grenze.bis;
    if (neu.endsWith(ueberhang)) neu = neu.slice(0, neu.length - ueberhang.length);
  }
  return wendeAn(segmente, [{ von: block.von, bis, neu }]);
}

const LEERRAUM = /\s/;

function zeilenumbrueche(text: string): number {
  return text.split('\n').length - 1;
}

/**
 * Entfernt den KERN der Spanne (ohne Leerraum an ihren Raendern) und hoechstens EINE der beiden
 * Luecken zu den Nachbarn -- die andere bleibt als Trennung stehen, sonst klebten die Nachbarn
 * aneinander. Nie ueber die Grenze des eigenen Abschnitts hinaus: Die Trennzeile zwischen zwei
 * Abschnitten gehoert `zusammen()` (und `sichereTrennungen`).
 *
 * Gemessen wird am Kern, nicht an `raw`: `marked` rechnet das "\n" hinter einem Absatz, auf den
 * direkt eine Liste folgt, dem Absatz zu. Frueher fiel es mit dem Block weg, waehrend die
 * Luecke davor ebenfalls entfernt wurde -- aus "A\n\nB\n- Liste" wurde so "A- Liste" (Fuzz-Befund
 * F1, strukturelle Runde).
 *
 * Welche Luecke bleibt: Stehen auf beiden Seiten Nachbarn, bleibt die mit MEHR Zeilenumbruechen
 * (eine Leerzeile trennt immer, ein einzelnes "\n" nur manchmal); bei Gleichstand wie bisher die
 * dahinter. Hat keine der beiden eine Leerzeile, bleiben beide stehen -- zusammen trennen sie
 * die Nachbarn dann mindestens so gut wie vorher jede fuer sich. Ohne Nachbarn auf einer Seite
 * faellt die Luecke dorthin weg, wo keiner steht: Der Abschnitt behaelt so seinen Abschluss
 * bzw. seinen Anfang (samt einem fuehrenden Trenn-"\n" aus `sichereTrennungen`).
 */
function mitLeerraum(text: string, grenzen: readonly Spanne[], von: number, bis: number): Spanne {
  const stueck = text.slice(von, bis);
  const kernVon = von + (stueck.length - stueck.trimStart().length);
  const kernBis = von + stueck.trimEnd().length;
  if (kernBis <= kernVon) return { von, bis };
  const vorne = grenzen[abschnittVon(grenzen, kernVon)];
  const hinten = grenzen[abschnittVon(grenzen, kernBis - 1)];
  if (!vorne || !hinten) return { von, bis };
  let anfang = kernVon;
  while (anfang > vorne.von && LEERRAUM.test(text[anfang - 1] ?? '')) anfang -= 1;
  let ende = kernBis;
  while (ende < hinten.bis && LEERRAUM.test(text[ende] ?? '')) ende += 1;
  const nachbarDavor = anfang > vorne.von;
  const nachbarDanach = ende < hinten.bis;
  if (nachbarDavor && nachbarDanach) {
    const vorn = zeilenumbrueche(text.slice(anfang, kernVon));
    const hintenUmbrueche = zeilenumbrueche(text.slice(kernBis, ende));
    if (vorn < 2 && hintenUmbrueche < 2) return { von: kernVon, bis: kernBis };
    return hintenUmbrueche < vorn ? { von: kernVon, bis: ende } : { von: anfang, bis: kernBis };
  }
  if (nachbarDavor) return { von: anfang, bis: kernBis };
  if (nachbarDanach) return { von: kernVon, bis: ende };
  return { von: anfang, bis: ende };
}

/**
 * Minor 4: fasst Blockspannen VOR `mitLeerraum` zusammen, wenn zwischen ihnen nur Leerraum
 * liegt und sie im selben Segment stehen. Ohne das reklamiert `mitLeerraum` denselben
 * Leerraum aus BEIDEN Richtungen unabhaengig voneinander -- der Leerraum, der eigentlich den
 * NAECHSTEN Nachbarn abtrennt, bleibt dann als verwaiste Leerzeile am Rand des Ergebnisses
 * stehen. Ueberlappende Spannen (z. B. Baustein-Region und ihr Inhalt) fasst diese Funktion
 * bewusst NICHT zusammen -- das erledigt `zuschneiden` weiterhin nach `mitLeerraum`.
 */
function gruppiere(
  rohSpannen: readonly Spanne[],
  text: string,
  grenzen: readonly Spanne[],
): Spanne[] {
  const sortiert = [...rohSpannen].sort((a, b) => a.von - b.von);
  const gruppen: Array<Spanne & { segment: number }> = [];
  for (const spanne of sortiert) {
    const segment = abschnittVon(grenzen, spanne.von);
    const letzte = gruppen[gruppen.length - 1];
    if (
      letzte &&
      letzte.segment === segment &&
      spanne.von >= letzte.bis &&
      text.slice(letzte.bis, spanne.von).trim() === ''
    ) {
      letzte.bis = Math.max(letzte.bis, spanne.bis);
    } else {
      gruppen.push({ ...spanne, segment });
    }
  }
  return gruppen.map(({ von, bis }) => ({ von, bis }));
}

/** Schneidet Bereiche an den Segmentgrenzen und fasst Ueberlappungen zusammen. */
function zuschneiden(bereiche: readonly Spanne[], grenzen: readonly Spanne[]): Spanne[] {
  const stuecke: Spanne[] = [];
  for (const bereich of bereiche) {
    for (const grenze of grenzen) {
      if (grenze.bis <= grenze.von) continue;
      const von = Math.max(bereich.von, grenze.von);
      const bis = Math.min(bereich.bis, grenze.bis);
      if (bis > von) stuecke.push({ von, bis });
    }
  }
  stuecke.sort((a, b) => a.von - b.von);
  const ergebnis: Spanne[] = [];
  for (const stueck of stuecke) {
    const letztes = ergebnis[ergebnis.length - 1];
    if (letztes && stueck.von <= letztes.bis) letztes.bis = Math.max(letztes.bis, stueck.bis);
    else ergebnis.push({ ...stueck });
  }
  return ergebnis;
}

/**
 * Marker-Regel (Spec §5): Baustein-Marker sind nie Teil eines Blocks. Sie verschwinden nur
 * zusammen, wenn der LETZTE Inhaltsblock ihres Bausteins entfernt wird -- auch dann, wenn
 * `[Beginn]` und `[Ende]` in zwei Abschnitten liegen (der n8n-Parser haengt `[Beginn ...]<br>`
 * gern an den vorherigen Abschnitt). Ein verwaister Marker kann so nicht entstehen.
 */
export function entferneBloecke(
  segmente: Segment[],
  zerlegung: Zerlegung,
  blockIndizes: readonly number[],
): Segment[] {
  pruefeZerlegung(segmente, zerlegung);
  const { text, bloecke, bausteine } = zerlegung;
  const grenzen = segmentGrenzen(segmente);
  const weg = new Set(blockIndizes);
  const rohSpannen: Spanne[] = [];
  for (const index of weg) {
    const block = bloecke[index];
    if (block) rohSpannen.push({ von: block.von, bis: block.bis });
  }
  bausteine.forEach((region, r) => {
    const inhalt = bloecke.filter((b) => b.baustein === r);
    if (inhalt.length > 0 && inhalt.every((b) => weg.has(b.index))) {
      rohSpannen.push({ von: region.von, bis: region.bis });
    }
  });
  if (rohSpannen.length === 0) return segmente;
  const bereiche = gruppiere(rohSpannen, text, grenzen).map((spanne) =>
    mitLeerraum(text, grenzen, spanne.von, spanne.bis),
  );
  return wendeAn(
    segmente,
    zuschneiden(bereiche, grenzen).map((b) => ({ ...b, neu: '' })),
  );
}

/**
 * Derselbe Text je Abschnitt wie im DOM der Textflaeche (`abschnittsTextIm`), aber ohne zu
 * rendern: Bloecke in Dokumentreihenfolge, davor jeweils die Baustein-Beschriftung beim
 * ersten Block eines Bausteins -- gezaehlt zum Abschnitt, in dem der Baustein BEGINNT.
 * Daran prueft die Seite, ob Anmerkungen noch gueltig sind (`istGueltig`).
 */
export function abschnittsTexte(
  zerlegung: Zerlegung,
  grenzen: readonly Spanne[],
  abschnittIds: readonly (string | undefined)[],
): Map<string, string> {
  const texte = new Map<string, string>();
  const haenge = (id: string | undefined, text: string) => {
    if (id) texte.set(id, (texte.get(id) ?? '') + text);
  };
  const gesehen = new Set<number>();
  for (const block of zerlegung.bloecke) {
    if (block.baustein !== null && !gesehen.has(block.baustein)) {
      gesehen.add(block.baustein);
      const region = zerlegung.bausteine[block.baustein];
      if (region) haenge(abschnittIds[abschnittVon(grenzen, region.von)], region.name);
    }
    haenge(abschnittIds[abschnittVon(grenzen, block.von)], textVonHtml(alsHtml(block.raw)));
  }
  // Ein leerer Abschnitt hat leeren Text, fehlt aber nicht -- sonst gaelte jede seiner
  // Anmerkungen als "Abschnitt unbekannt" statt als "zeigt auf keine Textstelle mehr".
  for (const id of abschnittIds) if (id && !texte.has(id)) texte.set(id, '');
  return texte;
}

/** Ein urspruenglicher Abschnitt (`urSegmente`-Index), der sich beim Vergleich geaendert hat. */
export interface SegmentAenderung {
  /** Index in `urSegmente`, NICHT im aktuellen `segmente` (siehe `herkunft`). */
  index: number;
  entfernt: boolean;
}

/**
 * Reine Vergleichsfunktion (Ruling R13-3/D7 aus dem B2-Preflight): Welche urspruenglichen
 * Abschnitte hat der Mensch gegenueber der aus n8n geladenen Fassung veraendert, und ist der
 * jeweilige Abschnitt jetzt leer (entfernt)? `absenden` in `freigabe-page.tsx` (Task 14) baut
 * `bearbeiteteAbschnitte` darauf, `textAenderungen` unten die Zusammenfassung in der
 * Rueckmeldespalte -- dieselbe Logik an genau einer Stelle statt zweimal wortgleich kopiert.
 *
 * Gezaehlt wird je URSPRUENGLICHEM Abschnitt (`herkunft[j]`), nicht je aktuellem Segment: Ein
 * Segment kann mehrere Abschnitte in sich vereinen, wenn `schneideAnUrsprung` beim Laden eines
 * Entwurfs einen Anker nicht wiedergefunden hat (Ruling 7, "ab der Schnittstelle gilt alles
 * Folgende als geaendert"). Aendert sich so ein verschmolzenes Segment, gelten ALLE darin
 * aufgegangenen Abschnitte als bearbeitet -- Spec §3.4 verlangt die Zaehlung "abgeleitet aus
 * bearbeiteteAbschnitte", also nach Abschnitts-IDs, nicht nach Segmenten.
 */
export function geaenderteSegmente(
  urSegmente: readonly Segment[],
  segmente: readonly Segment[],
  herkunft: readonly number[][],
): SegmentAenderung[] {
  const ergebnis: SegmentAenderung[] = [];
  herkunft.forEach((ursprungIndizes, segmentIndex) => {
    const segment = segmente[segmentIndex];
    if (!segment) return;
    const original = zusammen(ursprungIndizes.flatMap((i) => urSegmente[i] ?? []));
    if (segment.markdown === original) return;
    const entfernt = segment.markdown === '';
    ursprungIndizes.forEach((index) => ergebnis.push({ index, entfernt }));
  });
  return ergebnis;
}

/**
 * Zusammenfassung fuer die Rueckmeldespalte (Task 13, Spec §3.3): wie viele Abschnitte
 * bearbeitet bzw. entfernt wurden, gegenueber `urSegmente`. Baut auf `geaenderteSegmente` auf
 * (Ruling R13-3), damit ein verschmolzenes Segment hier genauso viele Abschnitte zaehlt wie in
 * `bearbeiteteAbschnitte`.
 */
export function textAenderungen(
  urSegmente: readonly Segment[],
  segmente: readonly Segment[],
  herkunft: readonly number[][],
): { bearbeitet: number; entfernt: number } {
  let bearbeitet = 0;
  let entfernt = 0;
  for (const aenderung of geaenderteSegmente(urSegmente, segmente, herkunft)) {
    if (aenderung.entfernt) entfernt += 1;
    else bearbeitet += 1;
  }
  return { bearbeitet, entfernt };
}

export function textAenderungenText({
  bearbeitet,
  entfernt,
}: {
  bearbeitet: number;
  entfernt: number;
}): string {
  const abschnitte = (n: number) => (n === 1 ? '1 Abschnitt' : `${n} Abschnitte`);
  if (bearbeitet === 0 && entfernt === 0) return 'Keine Textänderungen';
  if (entfernt === 0) return `${abschnitte(bearbeitet)} bearbeitet`;
  if (bearbeitet === 0) return `${abschnitte(entfernt)} entfernt`;
  return `${abschnitte(bearbeitet)} bearbeitet · ${entfernt} entfernt`;
}

/**
 * Beschriftung des Entfernen-Knopfs in der Auswahl-Leiste (Task 11): benennt den Umfang der
 * Auswahl nach Bausteinart bzw. Anzahl. Ein Abschnitt zaehlt nur, wenn seine Ueberschrift UND
 * alle seine Inhaltsbloecke ausgewaehlt sind -- der Vorspann vor der ersten Ueberschrift hat
 * keine eigene Ueberschrift und gilt der Nutzerin deshalb nie als "Abschnitt", sondern als
 * gewoehnlicher Text.
 */
export function entfernenBeschriftung(
  zerlegung: Zerlegung,
  grenzen: readonly Spanne[],
  blockIndizes: readonly number[],
): string {
  const gewaehlt = new Set(blockIndizes);
  if (gewaehlt.size === 0) return '';
  const jeAbschnitt = new Map<number, number[]>();
  for (const block of zerlegung.bloecke) {
    const abschnitt = abschnittVon(grenzen, block.von);
    jeAbschnitt.set(abschnitt, [...(jeAbschnitt.get(abschnitt) ?? []), block.index]);
  }
  // Ein Abschnitt zaehlt nur mit Ueberschrift -- der Vorspann davor ist fuer die Nutzerin
  // kein "Abschnitt", sondern einfach Text.
  const ganze = [...jeAbschnitt.values()].filter(
    (indizes) =>
      zerlegung.bloecke[indizes[0] ?? -1]?.art === 'ueberschrift' &&
      indizes.every((i) => gewaehlt.has(i)),
  );
  const inGanzen = new Set(ganze.flat());
  if (ganze.length > 0 && [...gewaehlt].every((i) => inGanzen.has(i))) {
    return ganze.length === 1 ? 'Abschnitt entfernen' : `${ganze.length} Abschnitte entfernen`;
  }
  if (gewaehlt.size === 1) {
    const art = zerlegung.bloecke[[...gewaehlt][0] ?? -1]?.art;
    if (art === 'ueberschrift') return 'Überschrift entfernen';
    if (art === 'liste') return 'Liste entfernen';
    return 'Absatz entfernen';
  }
  return `${gewaehlt.size} Blöcke entfernen`;
}
